import Cookies from 'js-cookie';
import axios, { type AxiosRequestHeaders } from 'axios';
import { logger } from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const CSRF_TOKEN_COOKIE = 'csrf-token';
const CSRF_TOKEN_HEADER = 'X-CSRF-Token';

/**
 * CSRF Protection Service
 * Manages CSRF tokens for the Double Submit Cookie pattern
 */
class CsrfService {
  private token: string | null = null;
  private fetchingToken: Promise<string> | null = null;

  /**
   * Get the current CSRF token
   * If no token exists, fetches a new one
   */
  async getToken(): Promise<string | null> {
    // If we're already fetching a token, wait for it
    if (this.fetchingToken) {
      try {
        return await this.fetchingToken;
      } catch (error) {
        logger.error('Failed to get CSRF token during fetch', error);
        return null;
      }
    }

    // Try to get token from memory first
    if (this.token) {
      return this.token;
    }

    // Try to get token from cookie
    this.setTokenFromCookie();
    if (this.token) {
      return this.token;
    }

    // No token found, fetch a new one
    try {
      return await this.fetchNewToken();
    } catch (error) {
      logger.error('Failed to get CSRF token', error);
      return null;
    }
  }

  /**
   * Fetch a new CSRF token from the server
   */
  async fetchNewToken(): Promise<string> {
    // Prevent multiple simultaneous fetches
    if (this.fetchingToken) {
      return this.fetchingToken;
    }

    this.fetchingToken = this._doFetchToken();

    try {
      const token = await this.fetchingToken;
      return token;
    } finally {
      this.fetchingToken = null;
    }
  }

  /**
   * Internal method to actually fetch the token
   */
  private async _doFetchToken(): Promise<string> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/csrf-token`, {
        withCredentials: true // Important: Include cookies in request
      });

      const { csrfToken } = response.data;

      if (!csrfToken) {
        throw new Error('No CSRF token in response');
      }

      // Store the token
      this.token = csrfToken;
      logger.debug('Fetched new CSRF token');

      return csrfToken;
    } catch (error) {
      logger.error('Error fetching CSRF token:', error);
      throw error;
    }
  }

  /**
   * Set token from cookie if available
   */
  setTokenFromCookie(): void {
    const cookieToken = Cookies.get(CSRF_TOKEN_COOKIE);
    if (cookieToken) {
      this.token = cookieToken;
      logger.debug('Loaded CSRF token from cookie');
    }
  }

  /**
   * Clear the current token
   */
  clearToken(): void {
    this.token = null;
    // Note: We don't clear the cookie as it's httpOnly=false
    // and managed by the server
    logger.debug('Cleared CSRF token from memory');
  }

  /**
   * Check if a request method requires CSRF protection
   */
  isStateChangingMethod(method?: string): boolean {
    const stateChangingMethods = ['post', 'put', 'patch', 'delete'];
    return stateChangingMethods.includes(method?.toLowerCase() || '');
  }

  /**
   * Add CSRF token to request headers
   */
  async addTokenToHeaders<T extends Record<string, any> | AxiosRequestHeaders | undefined>(
    headers?: T
  ): Promise<T extends undefined ? Record<string, any> : T> {
    const token = await this.getToken();

    // If no headers object exists yet, create one so callers can assign it back
    let targetHeaders: Record<string, any> | AxiosRequestHeaders;
    if (!headers) {
      targetHeaders = {};
    } else {
      targetHeaders = headers as Record<string, any> | AxiosRequestHeaders;
    }

    if (token) {
      const mutableHeaders = targetHeaders as Record<string, any> & { set?: (key: string, value: string) => void };

      if (typeof mutableHeaders.set === 'function') {
        mutableHeaders.set(CSRF_TOKEN_HEADER, token);
      } else {
        mutableHeaders[CSRF_TOKEN_HEADER] = token;
      }
    }

    return targetHeaders as T extends undefined ? Record<string, any> : T;
  }

  /**
   * Check if an error is a CSRF validation error
   */
  isCsrfError(error: any): boolean {
    return (
      error?.response?.status === 403 &&
      error?.response?.data?.code === 'CSRF_VALIDATION_FAILED'
    );
  }

  /**
   * Handle CSRF error by refreshing token
   */
  async handleCsrfError(): Promise<void> {
    logger.info('Handling CSRF error, fetching new token');
    await this.fetchNewToken();
  }
}

// Export singleton instance
export const csrfService = new CsrfService();

// Export the class for testing
export { CsrfService };

// Export helper functions for convenience
export const getCsrfToken = () => csrfService.getToken();
export const clearCsrfToken = () => csrfService.clearToken();
export const refreshCsrfToken = () => csrfService.fetchNewToken();
